import React, { useState } from "react";
import { Box, Paper, Typography, IconButton, Tooltip, Collapse, Chip, Button } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MergeIcon from "@mui/icons-material/Merge";
import { useSnackbar } from "notistack";
import { ChallengeTemplateDto } from "../../../api/types/dtos/challenge/ChallengeDto";
import MergeChallengesModal from "./MergeChallengesModal";

interface ChallengeTemplateSectionProps {
  templates: ChallengeTemplateDto[];
}

/** Clipboard copy with fallback for non-secure contexts (e.g. http://192.168.x.x). */
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to legacy method
  }
  // Legacy fallback: temporary textarea + execCommand('copy')
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

const ChallengeTemplatesSection: React.FC<ChallengeTemplateSectionProps> = ({ templates }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleCopyUuid = async (name: string, uuid: string) => {
    const success = await copyToClipboard(uuid);
    if (success) {
      enqueueSnackbar(`ID de "${name}" copiado`, { variant: "success" });
    } else {
      enqueueSnackbar("No se pudo copiar el ID", { variant: "error" });
    }
  };

  if (templates.length === 0) return null;

  return (
    <Paper elevation={1} sx={{ mb: 2, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          cursor: "pointer",
          userSelect: "none",
          backgroundColor: "action.hover",
        }}
        onClick={() => setCollapsed((c) => !c)}
        role="button"
        aria-label={collapsed ? "Expandir retos" : "Colapsar retos"}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Retos
          </Typography>
          <Chip size="small" label={templates.length} color="primary" variant="outlined" />
          {templates.length >= 2 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<MergeIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setMergeModalOpen(true);
              }}
            >
              Combinar
            </Button>
          )}
        </Box>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCollapsed((c) => !c); }}>
          {collapsed ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      </Box>
      <Collapse in={!collapsed}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 1.5 }}>
          {templates.map((tpl) => (
            <Box
              key={tpl.challengeId}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                pl: 1.5,
                pr: 0.5,
                py: 0.25,
                backgroundColor: "background.paper",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {tpl.name}
              </Typography>
              <Chip size="small" label={`${tpl.orgCount} org`} variant="outlined" />
              <Tooltip title={`Copiar ID de inspeccion: ${tpl.challengeId}`}>
                <IconButton
                  size="small"
                  onClick={() => handleCopyUuid(tpl.name, tpl.challengeId)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      </Collapse>
      <MergeChallengesModal
        open={mergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        templates={templates}
      />
    </Paper>
  );
};

export default ChallengeTemplatesSection;
